import html2pdf from "html2pdf.js"
import React from "react"
import {
  MdCheckCircle,
  MdDescription,
  MdDownload,
  MdEdit,
  MdEmail,
  MdFileCopy,
  MdFilePresent,
} from "react-icons/md"
import { Modal, ModalBody, ModalHeader } from "reactstrap"
import jobLogo from "../../assets/images/logo_transparent.png"
import moment from "moment"

const DownloadDocument = ({ userData, isOpen, setIsOpen }) => {
  // Static contract data
  const contractData = {
    title: "Contract of Employment",
    type: "Final Audit Report",
    date: "2024-05-25",
    created: moment(userData?.createdAt).format("DD/MM/YYYY"),
    status: userData?.hasConsented ? "Signed" : "Pending",
    transactionId: "CBJDHICAAbAALekQngrFR7ftAYWbwNbCSHNWVJOCCr",
    createdBy: {
      name: "J Kyle",
      email: "jkyle@jkylecleaningService.co.uk",
    },
  }

  const getDocumentHistory = () => {
    const baseTime = moment(userData?.signedDate)

    const fullHistory = [
      {
        icon: MdDescription,
        action: "Contract created by",
        user: contractData.createdBy.name,
        email: contractData.createdBy.email,
        timestamp: moment(userData?.createdAt).format("DD/MM/YYYY hh:mm A"),
      },
      {
        icon: MdEmail,
        action: "Policy viewed by",
        user: userData?.name || "Employee Name",
        email: userData?.email || "employee@example.com",
        timestamp: baseTime.format("DD/MM/YYYY hh:mm A"),
      },
      {
        icon: MdEdit,
        action: "Document e-signed by",
        user: userData?.name || "Employee Name",
        email: userData?.email || "employee@example.com",
        timestamp: baseTime
          .clone()
          .add(5, "minutes")
          .format("DD/MM/YYYY hh:mm A"),
      },
      {
        icon: MdCheckCircle,
        action: "Agreement completed",
        timestamp: baseTime
          .clone()
          .add(10, "minutes")
          .format("DD/MM/YYYY hh:mm A"),
      },
    ]

    // 👉 If hasConcerned = true → return only last 3
    if (!userData?.hasConsented) {
      return [fullHistory[0]]
    }

    return fullHistory
  }

  const handleDownloadPDF = () => {
    const element = document.getElementById("contract-content")

    if (!element) {
      console.error("Contract content not found")
      return
    }

    const fileName = `Contract_${userData?.name || "Employee"}_${
      contractData.date
    }.pdf`

    const options = {
      margin: [10, 10, 10, 10],
      filename: fileName,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        scrollY: 0,
      },
      jsPDF: {
        unit: "mm",
        format: "a4",
        orientation: "portrait",
      },
      pagebreak: { mode: ["css", "legacy"] },
    }

    html2pdf()
      .set(options)
      .from(element)
      .save()
      .catch(err => {
        console.error("PDF generation failed:", err)
      })
  }

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader toggle={() => setIsOpen(false)} className="border-bottom">
        <div className="d-flex align-items-center justify-content-between w-100 gap-3">
          <div className="d-flex align-items-center gap-3">
            <MdFileCopy size={28} className="text-white" />
            <div>
              <div className="fw-semibold fs-5">{contractData.title}</div>
              <small className="text-white">
                {userData?.name || "Employee Name"}
              </small>
            </div>
          </div>

          <button onClick={handleDownloadPDF} className="btn btn-primary">
            <MdDownload className="me-2" />
            Download PDF
          </button>
        </div>
      </ModalHeader>

      <ModalBody className="p-0">
        <div id="contract-content" className="p-8 bg-white">
          {/* Document Header */}
          <div className="border border-primary rounded p-4 mb-2">
            <div className="d-flex justify-content-between mb-3">
              <div>
                <h4 className="fw-bold mb-1">{contractData.title}</h4>
                <small className="text-muted">{contractData.type}</small>
              </div>
              <div className="w-25 d-flex justify-content-end">
                <img src={jobLogo} className="w-50 object-fit-cover" />
              </div>
            </div>

            <div className="bg-light border rounded p-3 pb-0">
              <div className="d-flex justify-content-between mb-1">
                <span className="text-muted">Created:</span>
                <span>{contractData.created}</span>
              </div>
              <div className="d-flex justify-content-between mb-1">
                <span className="text-muted">By:</span>
                <span>{contractData.createdBy.email}</span>
              </div>
              <div className="d-flex justify-content-between mb-1">
                <span className="text-muted">Status:</span>
                <span
                  className={`fw-semibold ${
                    userData?.hasConsented ? "text-success" : "text-warning"
                  }`}
                >
                  {contractData.status}
                </span>
              </div>
            </div>

            {/* Employee Information Section */}
            <div className="bg-light border rounded p-4 pt-2 mb-4">
              <h5 className="fw-semibold mb-3 d-flex align-items-center gap-2">
                <MdFilePresent className="text-primary" />
                Employee Details
              </h5>

              <div className="row g-3">
                <div className="col-md-4">
                  <medium className="text-muted">Full Name</medium>
                  <div className="fw-semibold">{userData?.name || "N/A"}</div>
                </div>
                <div className="col-md-4">
                  <medium className="text-muted">Email</medium>
                  <div className="fw-semibold">{userData?.email || "N/A"}</div>
                </div>

                {userData?.position && (
                  <div className="col-md-4">
                    <medium className="text-muted">Position</medium>
                    <div className="fw-semibold text-capitalize">
                      {userData.position}
                    </div>
                  </div>
                )}

                {userData?.department && (
                  <div className="col-md-6">
                    <small className="text-muted">Department</small>
                    <div className="fw-semibold">{userData.department}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Document History */}
            <h5 className="fw-bold border-bottom pb-2 mb-3">
              "{contractData.title}" History
            </h5>

            <div className="d-flex flex-column gap-3">
              {getDocumentHistory()?.map((item, index) => {
                const Icon = item.icon
                return (
                  <div
                    key={index}
                    className="d-flex gap-3 p-3 border rounded bg-light"
                  >
                    <div className="bg-primary bg-opacity-10 rounded-circle p-2">
                      <Icon className="text-primary" size={20} />
                    </div>

                    <div className="flex-grow-1">
                      <div>
                        <strong>{item.action}</strong>
                        {item.user && (
                          <>
                            {" "}
                            <span className="fw-semibold text-primary">
                              {item.user}
                            </span>{" "}
                            <span className="text-muted">({item.email})</span>
                          </>
                        )}
                      </div>

                      {item.note && (
                        <div className=" fst-italic text-muted">
                          {item.note}
                        </div>
                      )}

                      {item.timestamp && (
                        <div className=" text-muted font-monospace">
                          {item.timestamp}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Footer */}
            <div className="text-center border-top pt-3 mt-4">
              <small className="text-muted d-block">
                This is an electronically signed document. The signature is
                legally binding.
              </small>
            </div>
          </div>
        </div>
      </ModalBody>
    </Modal>
  )
}

export default DownloadDocument
